import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { IconPlus, IconCheck, IconSquare } from '@tabler/icons-react';

const DROPDOWN_WIDTH = 240;

const GroupDropdown = ({
 groups,
 creators,
 creatorGroupIds = [],
 onSelect,
 onUnassign,
 onCreateNew,
 onClose,
 position = 'bottom',
 anchorRef,
 inline = false
}) => {
 const dropdownRef = useRef(null);
 const [style, setStyle] = useState(inline ? {} : { position: 'fixed', opacity: 0, top: 0, left: 0, zIndex: 9999 });

 // Position the dropdown relative to anchor, clamped to viewport
 const reposition = useCallback(() => {
 if (inline) return; // Inline mode: no fixed positioning needed
 const anchor = anchorRef?.current;
 const dropdown = dropdownRef.current;
 if (!anchor || !dropdown) return;

 const anchorRect = anchor.getBoundingClientRect();
 const dropdownHeight = dropdown.scrollHeight;
 const pad = 8;

 // Prefer below; flip above if no room
 let top;
 const spaceBelow = window.innerHeight - anchorRect.bottom - pad;
 const spaceAbove = anchorRect.top - pad;

 if (position === 'top' || (spaceBelow < dropdownHeight && spaceAbove > spaceBelow)) {
 top = anchorRect.top - Math.min(dropdownHeight, spaceAbove) - 4;
 } else {
 top = anchorRect.bottom + 4;
 }

 // Horizontal: align right edge of dropdown to right edge of anchor, clamp to viewport
 let left = anchorRect.right - DROPDOWN_WIDTH;
 if (left < pad) left = pad;
 if (left + DROPDOWN_WIDTH > window.innerWidth - pad) {
 left = window.innerWidth - DROPDOWN_WIDTH - pad;
 }

 setStyle({
 position: 'fixed',
 top,
 left,
 width: DROPDOWN_WIDTH,
 maxHeight: Math.min(360, Math.max(spaceBelow, spaceAbove) - 4),
 zIndex: 9999,
 opacity: 1
 });
 }, [anchorRef, position, inline]);

 useLayoutEffect(() => {
 if (!inline) reposition();
 }, [reposition, inline]);

 // Close on outside click or escape
 useEffect(() => {
 const handleClickOutside = (e) => {
 if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
 anchorRef?.current && !anchorRef.current.contains(e.target)) {
 onClose();
 }
 };
 const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };

 document.addEventListener('mousedown', handleClickOutside);
 document.addEventListener('keydown', handleEscape);
 return () => {
 document.removeEventListener('mousedown', handleClickOutside);
 document.removeEventListener('keydown', handleEscape);
 };
 }, [onClose, anchorRef]);

 const getGroupFillStatus = (group) => {
 const assigned = creators.filter(c => c.groupId === group._id).length;
 const total = group.groupSize;
 return { assigned, total, isFull: assigned >= total };
 };

 return (
 <div
 ref={dropdownRef}
 className={`bg-surface-1 border border-glass-border overflow-y-auto ${inline ? 'w-full max-h-[280px]' : ''}`}
 style={inline ? undefined : style}
 >
 <div className="py-1">
 {groups.map(group => {
 const { assigned, total, isFull } = getGroupFillStatus(group);
 const isIn = creatorGroupIds.includes(group._id);
 const disabled = isFull && !isIn;

 return (
 <button
 key={group._id}
 onClick={() => {
 if (disabled) return;
 if (isIn) { onUnassign(group._id); }
 else { onSelect(group._id); }
 }}
 disabled={disabled}
 className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm ${
 disabled
 ? 'opacity-35 cursor-not-allowed'
 : isIn
 ? 'hover:bg-error-muted cursor-pointer'
 : 'hover:bg-glass-bg-hover cursor-pointer'
 }`}
 >
 {/* Checkbox indicator */}
 {isIn ? (
 <span className="flex-shrink-0 w-4 h-4 bg-accent flex items-center justify-center">
 <IconCheck size={12} className="text-white" strokeWidth={3} />
 </span>
 ) : (
 <span className="flex-shrink-0 w-4 h-4 border border-glass-border" />
 )}

 {/* Group name */}
 <span className={`flex-1 text-left truncate ${isIn ? 'text-text-primary font-medium' : 'text-text-secondary'}`}>
 {group.name}
 </span>

 {/* Fill count */}
 <span className={`flex-shrink-0 text-xs tabular-nums ${isFull && !isIn ? 'text-error' : 'text-text-tertiary'}`}>
 {assigned}/{total}
 </span>
 </button>
 );
 })}
 </div>

 <div className="border-t border-glass-border" />

 <button
 onClick={onCreateNew}
 className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-accent hover:bg-accent-muted cursor-pointer"
 >
 <IconPlus size={16} className="flex-shrink-0" />
 <span>New Group</span>
 </button>
 </div>
 );
};

export default GroupDropdown;
